import { cn } from '@workspace/ui/lib/utils';
import type React from 'react';

export interface FooterMenuItem {
  dataLink: string;
  to: string;
  label: string;
}

export interface FooterMenuColumn {
  title: string;
  items: FooterMenuItem[];
  /** Columns with the same mobileGroup are placed side-by-side on mobile */
  mobileGroup?: string;
}

export interface FooterPaymentLogo {
  alt: string;
  /** Image source. Used when `node` is not provided. */
  src?: string;
  width?: number;
  height?: number;
  className?: string;
  /** Pre-rendered payment icon (e.g. an <Icon /> component). Takes precedence over `src`. */
  node?: React.ReactNode;
}

export interface FooterSupportButton {
  /** Link target. Ignored when `onClick` is provided (the button becomes an action). */
  href?: string;
  /** When set, the button triggers this handler instead of navigating (e.g. opens a chat widget). */
  onClick?: () => void;
  label: string;
  icon: React.ReactNode;
  className?: string;
}

export interface FooterLogo {
  src: string;
  alt?: string;
  to?: string;
}

export interface FooterSocialLink {
  href: string;
  icon: React.ReactNode;
  label?: string;
}

export interface FooterVariant1Props {
  logo: FooterLogo;
  menuColumns: FooterMenuColumn[];
  paymentLogos?: FooterPaymentLogo[];
  socialLinks?: FooterSocialLink[];
  copyrightText: string;
  supportButton?: FooterSupportButton;
  /** Component for internal links. Defaults to <a>. Pass react-router Link for SPA navigation. */
  linkComponent?: React.ElementType;
  dataTheme?: string;
  className?: string;
}

const DefaultLink = ({
  to,
  children,
  ...props
}: { to?: string; children?: React.ReactNode } & Record<string, unknown>) => (
  <a href={to} {...props}>
    {children}
  </a>
);

const groupColumns = (columns: FooterMenuColumn[]) => {
  const groups: { key: string; columns: FooterMenuColumn[] }[] = [];
  const seen = new Map<string, number>();

  for (const col of columns) {
    const groupKey = col.mobileGroup;

    if (groupKey && seen.has(groupKey)) {
      groups[seen.get(groupKey)!].columns.push(col);
    } else {
      const idx = groups.length;
      groups.push({ key: groupKey || `single-${idx}`, columns: [col] });
      if (groupKey) {
        seen.set(groupKey, idx);
      }
    }
  }

  return groups;
};

const MenuColumn = ({
  column,
  LinkComp,
}: {
  column: FooterMenuColumn;
  LinkComp: React.ElementType;
}) => (
  <div className="flex-1">
    <div className="mb-4 text-[var(--navigation-text-primary)] [font:var(--text-300-semibold)] [letter-spacing:-0.16px] min-[1025px]:[font:var(--text-400-semibold)] min-[1025px]:[letter-spacing:-0.18px]">
      {column.title}
    </div>
    <div className="flex flex-col gap-2">
      {column.items.map((item) => (
        <LinkComp
          key={item.dataLink}
          data-link={item.dataLink}
          to={item.to}
          className="text-[var(--navigation-text-secondary)]! no-underline transition-colors duration-200 [font:var(--text-200-regular-high)] hover:text-[var(--navigation-text-primary)]! hover:no-underline"
        >
          {item.label}
        </LinkComp>
      ))}
    </div>
  </div>
);

const SocialLinks = ({ socialLinks }: { socialLinks: FooterSocialLink[] }) => (
  <div className="flex items-center gap-2">
    {socialLinks.map((social) => (
      <a
        key={social.label || social.href}
        href={social.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={social.label}
        className="flex size-10 shrink-0 items-center justify-center rounded-3 border border-[var(--navigation-stroke-primary)] text-[var(--navigation-text-primary)] transition-[background] duration-200 hover:bg-[var(--navigation-surface)]"
      >
        {social.icon}
      </a>
    ))}
  </div>
);

const SUPPORT_BUTTON_CLASSNAME =
  'flex h-10 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-3 border border-[var(--navigation-stroke-primary)] bg-transparent px-4 text-[var(--navigation-text-primary)] transition-[background] duration-200 [font:var(--text-200-semibold)] hover:bg-[var(--navigation-surface)] hover:text-[var(--navigation-text-primary)]';

const SupportButton = ({
  supportButton,
}: {
  supportButton: FooterSupportButton;
}) =>
  supportButton.onClick ? (
    <button
      type="button"
      onClick={supportButton.onClick}
      className={cn(SUPPORT_BUTTON_CLASSNAME, supportButton.className)}
    >
      {supportButton.icon}
      {supportButton.label}
    </button>
  ) : (
    <a
      href={supportButton.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(SUPPORT_BUTTON_CLASSNAME, supportButton.className)}
    >
      {supportButton.icon}
      {supportButton.label}
    </a>
  );

export const FooterVariant1 = ({
  logo,
  menuColumns,
  paymentLogos,
  socialLinks,
  copyrightText,
  supportButton,
  linkComponent,
  dataTheme,
  className,
}: FooterVariant1Props) => {
  const LinkComp = linkComponent || DefaultLink;
  const groups = groupColumns(menuColumns);

  return (
    <footer
      className={cn(
        // Use the app-level `--border-radius--650`/`--700` tokens: DubaiExpress
        // redeclares the shorthand `--border-radius--65`/`--70` smaller, and the
        // packages/ui bundle (loaded after the theme) would otherwise win.
        'relative z-[12] shrink-0 rounded-tl-[var(--border-radius--650,var(--border-radius--65))] rounded-tr-[var(--border-radius--650,var(--border-radius--65))] border border-[var(--navigation-stroke-primary)] [background:var(--navigation-bg)] min-[1025px]:rounded-tl-[var(--border-radius--700,var(--border-radius--70))] min-[1025px]:rounded-tr-[var(--border-radius--700,var(--border-radius--70))]',
        className,
      )}
      {...(dataTheme ? { 'data-theme': dataTheme } : {})}
    >
      {/* Top section: Logo + Menu */}
      <div className="p-6 min-[1025px]:px-[60px] min-[1025px]:pt-[60px] min-[1025px]:pb-0">
        <div className="min-[1025px]:mx-auto min-[1025px]:flex min-[1025px]:max-w-[1400px] min-[1025px]:items-start min-[1025px]:gap-[144px]">
          {/* Logo + Support button (mobile) */}
          <div className="flex items-center justify-between pb-2 min-[1025px]:shrink-0 min-[1025px]:pb-0">
            <LinkComp
              to={logo.to || '/'}
              className="inline-flex h-[30px] w-[122px] items-center min-[1025px]:h-[68px] min-[1025px]:w-[272px]"
            >
              <img
                src={logo.src}
                alt={logo.alt || ''}
                className="block size-full object-contain"
              />
            </LinkComp>
            <div className="block min-[1025px]:hidden">
              {supportButton && <SupportButton supportButton={supportButton} />}
            </div>
          </div>

          {/* Menu columns */}
          <div className="mt-6 flex flex-col gap-8 min-[1025px]:mt-0 min-[1025px]:flex-1 min-[1025px]:flex-row min-[1025px]:gap-0">
            {groups.map((group) =>
              group.columns.length > 1 ? (
                <div
                  key={group.key}
                  className="flex gap-4 min-[1025px]:flex-[2] min-[1025px]:gap-0"
                >
                  {group.columns.map((col) => (
                    <MenuColumn
                      key={col.title}
                      column={col}
                      LinkComp={LinkComp}
                    />
                  ))}
                </div>
              ) : (
                <MenuColumn
                  key={group.key}
                  column={group.columns[0]}
                  LinkComp={LinkComp}
                />
              ),
            )}
          </div>
        </div>
      </div>

      {/* Bottom section */}
      <div className="px-6 pb-6 min-[1025px]:px-[60px] min-[1025px]:pt-8 min-[1025px]:pb-12">
        <div className="flex flex-col gap-6 border-t border-t-[var(--navigation-stroke-secondary)] pt-6 min-[1025px]:mx-auto min-[1025px]:max-w-[1400px] min-[1025px]:flex-row min-[1025px]:items-center min-[1025px]:justify-between min-[1025px]:gap-0 min-[1025px]:pt-8">
          {paymentLogos && paymentLogos.length > 0 && (
            <div className="flex items-center gap-[23px]">
              {paymentLogos.map((pl) =>
                pl.node ? (
                  <span
                    key={pl.alt}
                    aria-label={pl.alt}
                    className={cn(
                      'flex items-center text-[var(--navigation-text-primary)]',
                      pl.className,
                    )}
                  >
                    {pl.node}
                  </span>
                ) : (
                  <img
                    key={pl.alt}
                    src={pl.src}
                    alt={pl.alt}
                    width={pl.width}
                    height={pl.height}
                    className={cn('object-contain brightness-0', pl.className)}
                  />
                ),
              )}
            </div>
          )}

          <div className="text-[var(--navigation-text-primary)] [font:var(--text-100-regular-high)] [letter-spacing:-0.12px] min-[1025px]:text-center">
            {new Date().getFullYear()}. {copyrightText}
          </div>

          <div className="flex items-center gap-4">
            {socialLinks && socialLinks.length > 0 && (
              <SocialLinks socialLinks={socialLinks} />
            )}
            <div className="hidden min-[1025px]:block">
              {supportButton && <SupportButton supportButton={supportButton} />}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterVariant1;
